import { Icon } from '../../shell/Icon.tsx';
import { Button } from '../Button.tsx';
import { duration, midEllipsis, utc } from '../format.ts';
import { useNow } from '../useNow.ts';
import styles from './gate.module.css';

/* ==========================================================================
   The console before there is a session.

   This is the one surface an operator sees when nothing else has rendered, so
   it is the console's first claim about what kind of software it is. The
   version it replaces was `padding: 4rem; text-align: center` and a sentence
   — a marketing page's idea of a gate, in an application whose whole argument
   is that it tells you the truth about a machine.

   So it is built as chrome: the 34px band, one 24px state strip, and the rail
   of facts whoever is debugging the redirect actually needs — authority,
   client, redirect URI, the verbatim provider error, and how long this has
   been going on. The rest of the viewport is hatched, because there genuinely
   is nothing there yet and drawing a placeholder would be the same lie the
   rest of this module exists to stop.
   ========================================================================== */

export type GateState = 'redirecting' | 'exchanging' | 'failed' | 'signed-out';

const LINE: Record<GateState, string> = {
  redirecting: 'Handing off to the identity provider.',
  exchanging: 'Exchanging the authorisation code for a session.',
  failed: 'Sign-in did not complete.',
  'signed-out': 'This session has ended.',
};

export function GateScreen({
  state,
  authority,
  clientId,
  redirectUri,
  provider = 'Google',
  reason = null,
  sinceMs = null,
  onRetry,
}: {
  state: GateState;
  authority: string | null;
  clientId: string | null;
  redirectUri: string;
  provider?: string | undefined;
  /** The provider's error, verbatim. Never paraphrased. */
  reason?: string | null | undefined;
  /** How long this state has held. Null while it is not worth saying. */
  sinceMs?: number | null | undefined;
  onRetry?: (() => void) | undefined;
}) {
  const now = useNow(true);
  const failed = state === 'failed';
  const host = authority === null ? null : hostOf(authority);
  const exchanging = state === 'exchanging';

  return (
    <div className={styles.gate}>
      <header className={styles.band}>
        <span className={styles.mark}>F3</span>
        <span className={styles.eyebrow}>Fleet · Command &amp; Control</span>
        <span className={styles.spacer} />
        <span className={styles.clock}>{utc(new Date(now).toISOString())}</span>
      </header>

      <div className={styles.strip} data-tone={failed ? 'error' : 'nominal'} role="status">
        <span className={styles.tick} />
        <span className={styles.stripIcon}>
          <Icon name={failed ? 'lock' : 'key'} size={13} />
        </span>
        <span className={styles.headline}>{LINE[state]}</span>
        <span className={styles.detail}>
          {reason ?? (host === null ? 'no identity provider configured' : `${provider.toLowerCase()} via ${host}`)}
          {sinceMs !== null && sinceMs > 4_000 ? `  ·  ${duration(sinceMs)}` : ''}
        </span>
        <span className={styles.spacer} />
        {onRetry !== undefined && (
          <Button size="sm" variant={failed ? 'primary' : 'secondary'} onClick={onRetry}>
            {failed ? 'Retry sign-in' : 'Start again'}
          </Button>
        )}
      </div>

      {/* The console's loading idiom, unchanged: a 1px indeterminate bar under
          the header of the thing that is working. Absent once it has failed —
          nothing is in flight then, and a bar that keeps sweeping over a dead
          redirect is the console pretending to try. */}
      {!failed && <div className={styles.bar} role="progressbar" aria-label="Signing in" />}

      <div className={styles.body}>
        {/* Every fact there is before a session exists, in one band across the
            top: which step stopped, and what this build is pointed at. */}
        <div className={styles.facts}>
          {/* WHICH STEP FAILED.

              A sign-in is a four-step handshake and "Sign-in did not complete"
              does not say where it stopped. The ladder does, off the gate's
              own state machine and nothing else: whether a code came back in
              the query string, whether the exchange was attempted, and whether
              the provider has now returned twice without a session. It is the
              difference between a misconfigured callback URL and a blocked
              third-party cookie. */}
          <section className={styles.group}>
            <div className={styles.groupHead}>
              <span className={styles.groupLabel}>Handshake</span>
              <span className={styles.groupAside}>{provider.toLowerCase()}</span>
            </div>
            {STEPS.map((step, i) => (
              <div
                key={step.id}
                className={styles.step}
                data-state={stepState(i, state, exchanging)}
              >
                <span className={styles.stepTick} aria-hidden="true" />
                <span className={styles.stepIndex}>{i + 1}</span>
                <span className={styles.label}>{step.label}</span>
                <span className={styles.spacer} />
                <span className={styles.stepState}>
                  {stepWord(stepState(i, state, exchanging))}
                </span>
              </div>
            ))}
          </section>

          <section className={styles.group}>
            <div className={styles.groupHead}>
              <span className={styles.groupLabel}>Configuration</span>
              <span className={styles.groupAside}>build-time</span>
            </div>
            <Row label="Identity provider" value={provider} />
            <Row label="Authority" value={host} full={authority} />
            <Row label="Client" value={clientId} full={clientId} />
            <Row label="Flow" value="authorization_code + PKCE" />
          </section>

          <section className={styles.group}>
            <div className={styles.groupHead}>
              <span className={styles.groupLabel}>This attempt</span>
              <span className={styles.groupAside}>{state}</span>
            </div>
            <Row
              label="Elapsed"
              value={sinceMs === null ? null : duration(sinceMs)}
            />
            <Row label="Redirect" value={pathOf(redirectUri)} full={redirectUri} />
            <Row
              label="Provider said"
              value={reason === null ? 'nothing' : reason}
              full={reason}
            />
            <Row label="Code in query" value={exchanging ? 'yes' : 'no'} />
          </section>
        </div>

        {/* The one line, where the console's first row would be. The ground
            under it is ground: there is genuinely nothing to draw before a
            session exists, and a hatched panel over 900px of it would be
            promising content that is not coming. */}
        <p className={styles.note}>
          <span className={styles.noteLabel}>No session.</span>
          <span className={styles.noteDetail}>
            {host === null
              ? 'VITE_COGNITO_AUTHORITY is not set on this build'
              : `nothing renders until ${host} returns a code`}
          </span>
        </p>
      </div>

      <footer className={styles.foot}>
        {host === null ? 'no identity provider configured' : host}
        {'  ·  '}
        {pathOf(redirectUri)}
        {'  ·  '}
        authorization_code + PKCE
      </footer>
    </div>
  );
}

const STEPS = [
  { id: 'redirect', label: 'Redirect to provider' },
  { id: 'consent', label: 'Provider returns a code' },
  { id: 'exchange', label: 'Exchange code for tokens' },
  { id: 'session', label: 'Session established' },
] as const;

type StepState = 'done' | 'active' | 'failed' | 'waiting';

/**
 * Where the handshake is, from the gate's own state and nothing else. A step
 * before the current one is done, the current one is in flight — or failed,
 * if the gate has stopped — and the rest have not been reached. No step is
 * ever marked done on faith.
 */
function stepState(i: number, state: GateState, exchanging: boolean): StepState {
  /* A session that has ENDED has no handshake in flight — the ladder is the
     next attempt's, and none of it has happened. Printing `in flight` against
     step 1 would be the gate claiming work it is not doing. */
  if (state === 'signed-out') return 'waiting';
  const at = exchanging ? 2 : state === 'failed' ? 1 : 0;
  if (i < at) return 'done';
  if (i > at) return 'waiting';
  return state === 'failed' ? 'failed' : 'active';
}

function stepWord(s: StepState): string {
  return s === 'done' ? 'done' : s === 'active' ? 'in flight' : s === 'failed' ? 'stopped' : '—';
}

function Row({
  label,
  value,
  full,
}: {
  label: string;
  value: string | null;
  full?: string | null | undefined;
}) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value} title={full ?? value ?? undefined}>
        {value === null ? '—' : midEllipsis(value, 30)}
      </span>
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

function pathOf(url: string): string {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname}`;
  } catch {
    return url;
  }
}
